import { Box, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import { Link } from 'react-router-dom';
import SearchTextField from '../../components/SearchTextField';
import TableSkeleton from '../../components/TableSkeleton';
import { useWriters } from '../../hooks/queries/useWriter';
import { useQueryStringState } from '../../hooks/useQueryStringState';
import { IWriter } from '../../services/types';
import { RouteNames } from '../config';

interface IProps {
  onRowClick: (writer: IWriter) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  search: {
    width: 360,
    marginBottom: 20,
  },

  idTableCell: {
    width: 80,
  },

  joinedAtTableCell: {
    width: 180,
  },

  tableRow: {
    height: 52,
  },
}));

const WriterAccountList = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { onRowClick } = props;

  const [searchKey, setSearchKey] = useQueryStringState<string>('searchKey');
  const { data, isLoading } = useWriters(searchKey);

  const writers = data?.items;

  const handleRowClick = (writer: IWriter) => () => onRowClick(writer);

  const handleSearchSubmit = (query: string) => {
    setSearchKey(query);
  };

  const handleSearchValueChange = (query: string) => {
    if (!query) {
      handleSearchCancel();
    }
  };

  const handleSearchCancel = () => {
    setSearchKey(undefined);
  };

  return (
    <Box>
      <SearchTextField
        defaultValue={searchKey}
        onValueChange={handleSearchValueChange}
        placeholder="Search a Writer's Account"
        className={classes.search}
        onValueSubmit={handleSearchSubmit}
        onCancel={handleSearchCancel}
      />
      <Paper>
        <Table>
          <TableHead>
            <TableCell align="center" padding="none" className={classes.idTableCell}>
              User ID
            </TableCell>
            <TableCell>Writer&#39;s Account</TableCell>
            <TableCell>Assigned Pen Names</TableCell>
            <TableCell>Pen Names with Editing Rights</TableCell>
            <TableCell className={classes.joinedAtTableCell}>Joined At</TableCell>
          </TableHead>
          <TableBody>
            {isLoading && <TableSkeleton rows={20} columns={5} />}
            {!isLoading &&
              writers &&
              writers.map(writer => {
                const { id, username, licensedPennames, ownedPennames, joinedAt } = writer;

                return (
                  <TableRow
                    key={id}
                    component={Link}
                    to={`${RouteNames.WriterAccount}/v/${id}`}
                    hover
                    onClick={handleRowClick(writer)}
                    className={classes.tableRow}
                  >
                    <TableCell align="center" padding="none">
                      {id}
                    </TableCell>
                    <TableCell>{username}</TableCell>
                    <TableCell>{!isEmpty(licensedPennames) && licensedPennames.map(({ name }) => name).join(', ')}</TableCell>
                    <TableCell>{!isEmpty(ownedPennames) && ownedPennames.map(({ name }) => name).join(', ')}</TableCell>
                    <TableCell>{moment(joinedAt).format('LLL')}</TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        {!isLoading && isEmpty(writers) && (
          <Box width="100%" height="156px" display="flex" alignItems="center" justifyContent="center">
            <Typography variant="body1">
              {writers === undefined
                ? 'To see a Writer’s Web account information, search a writer’s account or user ID.'
                : 'This account does not exist. Please try again.'}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default WriterAccountList;
